import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  DropdownMenu,
  TextIconButton,
  SortDownIcon,
  Tabs,
} from 'shared-portal-component';

import ActivityItem from '@/components/molecules/ActivityItem/ActivityItem';
import ParticipantItem from '@/components/molecules/ParticipantItem/ParticipantItem';
import SearchInput from '@/components/molecules/SearchInput/SearchInput';
import '@/components/organism/SixDetail/ActivityFeed/ActivityFeed.scss';
import type { SixActivity } from '@/types/six/Six';
import type { ParticipantsResponse } from '@/types/SixDetail.types';

function formatActivityTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

const TAB_ITEMS = [
  { label: 'Activity', value: 'activity' },
  { label: 'Participants', value: 'participants' },
];

interface ActivityFeedProps {
  activities: SixActivity[];
  participants: ParticipantsResponse;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ activities, participants }) => {
  const [activeTab, setActiveTab] = useState('activity');
  const [sortOpen, setSortOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSort = (_sortType: string) => {
    // TODO: Implement sorting logic
  };

  const SORT_MENU_ITEMS = [
    {
      label: 'Oldest',
      value: 'old',
      onClick: () => {
        handleSort('create');
      },
    },
    {
      label: 'Newest',
      value: 'new',
      onClick: () => {
        handleSort('upload');
      },
    },
  ];

  return (
    <Container>
      {/* Tabs Row */}
      <Row spacing="md" justify="center" className="space">
        <Col size={12} className="text-center">
          <Tabs
            tabItems={TAB_ITEMS}
            onClick={setActiveTab}
            value={activeTab}
            defaultActiveTab="activity"
          />
        </Col>
      </Row>

      {/* Search and sort row */}
      <Row spacing="none" className="activity-field-header-row" justify='between'>
        <Col size="content">
          <SearchInput
            placeholder="Search the log"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />
        </Col>
        <Col size="content">
          <div className="relative">
            <TextIconButton
              icon={<SortDownIcon />}
              text="Sort the log"
              size="sm"
              variant="filled"
              onClick={() => {
                setSortOpen((prev) => !prev);
              }}
            />
            <DropdownMenu
              open={sortOpen}
              menuItems={SORT_MENU_ITEMS}
              onClose={() => {
                setSortOpen(false);
              }}
            />
          </div>
        </Col>
      </Row>

      {/* Activity List */}
      {activeTab === 'activity' && (
        <Row spacing="md" className="space">
          <Col size={12}>
            <ul className="activity-list">
              {activities.map((activity, idx) => (
                <li key={`${activity.id}-${idx}`}>
                  <ActivityItem
                    title={activity.userName}
                    time={formatActivityTimestamp(activity.timestamp)}
                    files={activity.description}
                  />
                </li>
              ))}
            </ul>
          </Col>
        </Row>
      )}

      {/* Participants List */}
      {activeTab === 'participants' && (
        <Row spacing="md" className="space">
          <Col size={12}>
            <div className="participants-list">
              {participants.owners.length > 0 && (
                <div className="participants-section">
                  <h3 className="participants-section-title">Owners</h3>
                  {participants.owners.map((owner) => (
                    <ParticipantItem
                      key={owner.id}
                      participant={owner}
                      role="Owner"
                    />
                  ))}
                </div>
              )}

              {participants.internalParticipants.length > 0 && (
                <div className="participants-section">
                  <h3 className="participants-section-title">Internal Participants</h3>
                  {participants.internalParticipants.map((participant) => (
                    <ParticipantItem
                      key={participant.id}
                      participant={participant}
                      role="Internal"
                    />
                  ))}
                </div>
              )}

              {participants.externalParticipants.length > 0 && (
                <div className="participants-section">
                  <h3 className="participants-section-title">External Participants</h3>
                  {participants.externalParticipants.map((participant) => (
                    <ParticipantItem
                      key={participant.id}
                      participant={participant}
                      role="External"
                    />
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ActivityFeed;
